"use client"

import { useState, useEffect, useCallback, useRef } from "react"
import { getQueueCount, processQueue as processQueueService, setQueueUpdateCallback } from "../services/queue"
import { useNetworkStatus } from "./useNetworkStatus"

export const useQueueManager = (
  onQueueProcessed?: () => void,
  setQueueProcessing?: (processing: boolean) => void
) => {
  const [queueCount, setQueueCount] = useState(0)
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastSync, setLastSync] = useState<number | null>(null)
  const isOnline = useNetworkStatus()
  const processingRef = useRef(false)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)


  const updateQueueCount = useCallback(async () => {
    try {
      const count = await getQueueCount()
      setQueueCount(count)
      console.log(`🔢 Contador de cola actualizado: ${count} elementos`)
    } catch (err) {
      console.error("❌ Error obteniendo contador de cola:", err)
    }
  }, [])

  const processQueue = useCallback(async () => {
    // Prevent multiple simultaneous processing
    if (processingRef.current || !isOnline) {
      console.log("⏸️ Procesamiento ya en curso o sin conexión")
      return
    }

    processingRef.current = true
    setIsProcessing(true)
    setError(null)

    // Notify asistentes hook that queue processing started
    setQueueProcessing?.(true)

    try {
      console.log("🔄 Iniciando procesamiento de cola...")
      const result = await processQueueService()

      if (result.success) {
        console.log(`✅ Cola procesada exitosamente. ${result.processed} elementos procesados`)
        setLastSync(Date.now())
        setError(null)

        // Trigger data refresh if callback provided and queue is now empty
        if (onQueueProcessed && result.processed > 0) {
          const remainingCount = await getQueueCount()
          if (remainingCount === 0) {
            console.log("🔄 Cola vacía, activando actualización de datos...")
            setTimeout(() => onQueueProcessed(), 1000) // Small delay to ensure server processing
          }
        }
      } else {
        console.error("❌ Error procesando cola:", result.error)
        setError(result.error || "Error desconocido")
      }

      // Always update queue count after processing
      await updateQueueCount()
    } catch (err) {
      console.error("❌ Error en processQueue:", err)
      setError(err instanceof Error ? err.message : "Error desconocido")
    } finally {
      setIsProcessing(false)
      processingRef.current = false
      
      // Notify asistentes hook that queue processing finished
      setQueueProcessing?.(false)
    }
  }, [isOnline, updateQueueCount, onQueueProcessed, setQueueProcessing])

  // Clear interval when component unmounts or when going offline
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [])

  // Auto-process queue every 15 seconds when online
  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }

    if (!isOnline) {
      console.log("📴 Sin conexión - pausando procesamiento automático")
      return
    }

    console.log("🌐 Conexión disponible - iniciando procesamiento automático")

    intervalRef.current = setInterval(async () => {
      if (!processingRef.current) {
        const count = await getQueueCount()
        if (count > 0) {
          console.log(`⏰ Procesamiento automático: ${count} elementos en cola`)
          processQueue()
        }
      }
    }, 15000)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [isOnline, processQueue])

  // Process queue immediately when coming back online (only once)
  useEffect(() => {
    let timeoutId: NodeJS.Timeout

    if (isOnline && !processingRef.current) {
      timeoutId = setTimeout(async () => {
        const count = await getQueueCount()
        if (count > 0) {
          console.log("🌐 Conexión restaurada, procesando cola...")
          processQueue()
        }
      }, 1000) // Small delay to avoid race conditions
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [isOnline, processQueue])

  // Update queue count on mount
  useEffect(() => {
    updateQueueCount()
  }, [updateQueueCount])

  // Registrar callback para actualizaciones de cola (incluyendo offline)
  useEffect(() => {
    const callback = () => {
      console.log("🔔 Notificación de cambio en cola recibida")
      updateQueueCount().catch(console.error)
    }
    setQueueUpdateCallback(callback)
    return () => {
      setQueueUpdateCallback(() => {})
    }
  }, [updateQueueCount])

  // Forzar actualización inicial
  useEffect(() => {
    updateQueueCount()
  }, [])

  return {
    queueCount,
    isProcessing,
    error,
    lastSync,
    processQueue,
    updateQueueCount,
  }
}
